import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AvailabilityConflict,
  AvailabilityResponse,
  BookingService,
  CheckEmployeeAvailabilityRequest,
  EmployeeProcedureService,
  EmployeeService,
  ServiceService,
} from '@beautyapp/core';
import {
  InputModule,
  SelectComponent,
  TimePickerComponent,
} from '@beautyapp/form';
import { VisitAddFacade } from '@beautyapp/shared';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { format } from 'date-fns';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  lastValueFrom,
  of,
  throwError,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { createDateRangeValidator } from '../../../validators/appointment.validator';

@UntilDestroy()
@Component({
  selector: 'beautyapp-edit-booking-info',
  imports: [
    NzModalModule,
    NzTableModule,
    TranslateModule,
    NzSpaceModule,
    InputModule,
    SelectComponent,
    TimePickerComponent,
    ReactiveFormsModule,
    AsyncPipe,
    NzButtonComponent,
    NzAlertModule,
  ],
  templateUrl: './edit-booking-info.component.html',
  styleUrl: './edit-booking-info.component.scss',
})
export class EditBookingInfoComponent implements OnInit {
  data = inject(NZ_MODAL_DATA);
  modalRef = inject(NzModalRef);
  serviceService: ServiceService = inject(ServiceService);
  employeeServiceService: EmployeeProcedureService = inject(
    EmployeeProcedureService
  );
  visitAddFacade = inject(VisitAddFacade);
  bookingService = inject(BookingService);
  employeeService = inject(EmployeeService);
  permissionsService = inject(NgxPermissionsService);

  /** Loading state for async operations */
  loading = signal(false);

  /** Availability check signals */
  availabilityConflicts = signal<AvailabilityConflict[]>([]);
  isCheckingAvailability = signal(false);
  hasAvailabilityConflicts = computed(
    () => this.availabilityConflicts().length > 0
  );
  /** Flag to track if user has seen the warning and wants to proceed */
  proceedDespiteConflicts = signal(false);

  /**
   * Checks if current user has Employee permission
   */
  get isEmployee() {
    return !this.permissionsService.getPermission('calendar.view_all');
  }

  /**
   * Resource for loading booking data
   */
  bookingResource = rxResource({
    params: () => ({ bookingId: this.data?.booking?.id }),
    stream: ({ params }) =>
      params.bookingId ? this.bookingService.getById(params.bookingId) : EMPTY,
  });

  /**
   * Current booking data
   */
  booking = computed(() => this.bookingResource.value());

  employee = signal<any | null>(null);

  /**
   * Form for booking data
   */
  form = new FormGroup(
    {
      startTime: new FormControl<Date>(
        new Date(this.data.startTime),
        Validators.required
      ),
      endTime: new FormControl<Date | null>(null, Validators.required),
      employeeId: new FormControl(null, Validators.required),
    },
    {
      validators: [createDateRangeValidator()],
    }
  );

  constructor() {
    effect(() => {
      if (this.booking()) {
        // Create a form value object to be patched
        const formValues: any = {
          startTime: this.booking().startTimeDate,
          endTime: this.booking().endTimeDate,
          employeeId: this.booking().employeeId,
        };

        this.form.patchValue(formValues);
      }
    });
  }

  ngOnInit(): void {
    console.log('data', this.data);
    if (!this.data.visitId) {
      this.form.get('employeeId')?.setValue(this.data.employeeId);
      this.form.get('startTime')?.setValue(this.data.booking.startTimeDate);
      this.form.get('endTime')?.setValue(this.data.booking.endTimeDate);
    }

    this.form
      .get('employeeId')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        filter((employeeId) => !!employeeId && employeeId !== 'null'),
        distinctUntilChanged(),
        switchMap((employeeId) => this.employeeService.getById(employeeId))
      )
      .subscribe((employee) => {
        this.employee.set(employee);
      });
  }

  /**
   * Prepares booking data from form values
   */
  private prepareBookingData() {
    const { startTime, endTime, employeeId } = this.form.value;

    return {
      employeeId,
      startTime,
      endTime,
    };
  }

  /**
   * Handles errors and resets loading state
   */
  private handleError(error: any) {
    this.loading.set(false);
    return throwError(() => error);
  }

  /**
   * Handles successful operations
   */
  private handleSuccess() {
    this.modalRef.close();
    this.loading.set(false);
  }

  /**
   * Checks availability for employee
   * Returns true if available, false if there are conflicts
   */
  async checkAvailability(): Promise<boolean> {
    const { employeeId, startTime, endTime } = this.form.value;
    const bookingDate =
      this.booking()?.bookingDate || this.data?.booking?.bookingDate;

    if (!employeeId || !startTime || !endTime || !bookingDate) {
      return true; // Skip check if required fields are missing
    }

    this.isCheckingAvailability.set(true);
    this.availabilityConflicts.set([]);

    try {
      const params: CheckEmployeeAvailabilityRequest = {
        employeeId,
        date:
          typeof bookingDate === 'string'
            ? bookingDate
            : format(bookingDate, 'yyyy-MM-dd'),
        startTime: format(startTime, 'HH:mm'),
        endTime: format(endTime, 'HH:mm'),
        excludeBookingId:
          this.booking()?.id || this.data?.booking?.id || undefined,
      };

      const response = await lastValueFrom(
        this.bookingService.checkEmployeeAvailability(params).pipe(
          catchError((error) => {
            console.error('Availability check failed:', error);
            return of({
              available: true,
              conflicts: [],
            } as AvailabilityResponse);
          })
        )
      );

      this.availabilityConflicts.set(response.conflicts);
      return response.available;
    } finally {
      this.isCheckingAvailability.set(false);
    }
  }

  /**
   * Gets formatted conflict message for display
   */
  getConflictMessage(conflict: AvailabilityConflict): string {
    if (conflict.reason) {
      return `${conflict.name}: ${conflict.reason}`;
    }
    if (conflict.conflictingBooking) {
      return `${conflict.name}: კონფლიქტი ჯავშანთან ${
        conflict.conflictingBooking.startTime
      }-${conflict.conflictingBooking.endTime}${
        conflict.conflictingBooking.serviceName
          ? ` (${conflict.conflictingBooking.serviceName})`
          : ''
      }`;
    }
    return conflict.name;
  }

  /**
   * Confirms proceeding despite availability conflicts
   */
  confirmDespiteConflicts() {
    this.proceedDespiteConflicts.set(true);
    this.submit();
  }

  /**
   * Handles form submission for creating or updating a booking
   */
  async submit() {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    // If user hasn't acknowledged conflicts yet, check availability first
    if (!this.proceedDespiteConflicts()) {
      const isAvailable = await this.checkAvailability();
      if (!isAvailable) {
        // Conflicts found - show warning and wait for user to confirm
        return;
      }
    }

    this.loading.set(true);

    const bookingData = this.prepareBookingData() as any;

    if (this.data.visitId) {
      this.visitAddFacade
        .updateVisitBooking({
          id: this.booking().id,
          visitId: this.booking().visitId,
          startTime: bookingData.startTime,
          endTime: bookingData.endTime,
          employeeId: bookingData.employeeId,
        })
        .pipe(
          untilDestroyed(this),
          catchError((error) => this.handleError(error))
        )
        .subscribe(() => this.handleSuccess());
    } else {
      this.modalRef.close({
        ...bookingData,
        employee: this.employee(),
        startTimeDate: bookingData.startTime,
        endTimeDate: bookingData.endTime,
      });
    }
  }
}
